import { useCallback, useRef, useState } from "react";

type ThrottleOptions = {
  throttleTimes: number[];
  delayReset: number;
};

const DEFAULT_OPTION: ThrottleOptions = {
  throttleTimes: [10, 15, 30, 60],
  delayReset: 65,
};

function useThrottle<T extends (...args: unknown[]) => void>(
  callback: T,
  options: Partial<ThrottleOptions> = {}
): [T, boolean] {
  const { throttleTimes, delayReset } = { ...DEFAULT_OPTION, ...options };
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timesCalled = useRef<number>(0);
  const lastCallRef = useRef<number>(0);
  const [isThrottled, setIsThrottled] = useState(false);

  const throttleFunc = useCallback(
    ((...args: Parameters<T>) => {
      const now = Date.now();
      const elapsedSeconds = (now - lastCallRef.current) / 1000;

      const currentThrottle =
        throttleTimes[Math.min(timesCalled.current, throttleTimes.length - 1)];

      const lastThrottle =
        timesCalled.current > 0 ? throttleTimes[timesCalled.current - 1] : throttleTimes[0];

      const shouldRunFirstTime = timesCalled.current === 0;
      const shouldRunAfterThrottle = elapsedSeconds >= lastThrottle;

      if (shouldRunFirstTime || shouldRunAfterThrottle) {
        setIsThrottled(true);
        callback(...args);
        lastCallRef.current = now;
        timesCalled.current += 1;
      }

      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => {
        timesCalled.current = 0;
        setIsThrottled(false);
      }, delayReset * 1000);

      setTimeout(() => setIsThrottled(false), currentThrottle * 1000);
    }) as T,
    [callback, throttleTimes, delayReset]
  );

  return [throttleFunc, isThrottled];
}

export default useThrottle;
